/**
 * Gateway error envelope -> Hebrew message, for the password-change surfaces.
 *
 * The envelope is `APIResponse` (smartnet_api_gateway, app/dto/APIResponse.java):
 * `{ success: false, data: null, message, error, statusCode }`, where `error`
 * carries the code that `GlobalExceptionHandler` picked. An exception that no
 * handler catches never reaches that class, so the body is Spring's default one
 * with no `error` and no `message` — only the HTTP status is left to read.
 */

type GatewayErrorBody = {
  error?: string | { code?: string | null; message?: string | null } | null
  message?: string | null
  statusCode?: number | null
}

const HEBREW = /\p{Script=Hebrew}/u

const WRONG_CURRENT_PASSWORD = 'הסיסמה הנוכחית שגויה'
const PASSWORD_REUSED = 'הסיסמה החדשה שימשה בעבר. נא לבחור סיסמה אחרת'
const UNKNOWN_USER = 'המשתמש לא נמצא. נא להתחבר מחדש'
const SESSION_ENDED = 'פג תוקף ההתחברות. נא להתחבר מחדש'
const SYSTEM_FAULT =
  'שינוי הסיסמה נכשל בגלל תקלה בהגדרות המערכת. נא לפנות לתמיכה'
const SERVER_FAULT = 'שגיאה בשרת. נא לנסות שוב מאוחר יותר או לפנות לתמיכה'
const NO_CONNECTION = 'אין חיבור לשרת. נא לבדוק את החיבור ולנסות שוב'
const GENERIC = 'שגיאה בשינוי הסיסמה. נא לנסות שוב'

// `POST /api/auth/modify-password` is the only caller, so each code is read in
// that endpoint's meaning: FORBIDDEN is AuthenticationException on the old
// password, INVALID is the password-history check.
const MESSAGE_BY_CODE: Record<string, string> = {
  FORBIDDEN: WRONG_CURRENT_PASSWORD,
  INVALID: PASSWORD_REUSED,
  USER_NOT_FOUND: UNKNOWN_USER,
  INTERNAL_ERROR: SERVER_FAULT,
}

const readBody = (data: unknown): GatewayErrorBody =>
  data !== null && typeof data === 'object' ? (data as GatewayErrorBody) : {}

const readCode = (body: GatewayErrorBody): string => {
  const { error } = body
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') return error.code ?? ''
  return ''
}

const readMessage = (body: GatewayErrorBody): string => {
  const { error } = body
  if (error && typeof error === 'object' && error.message) return error.message
  return body.message ?? ''
}

export const passwordChangeErrorMessage = (err: unknown): string => {
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response
  if (!response) return NO_CONNECTION

  const body = readBody(response.data)
  const code = readCode(body).toUpperCase()

  const mapped = MESSAGE_BY_CODE[code]
  if (mapped) return mapped
  // The Gateway's database-integrity handler is newer than any released
  // Gateway, so match the family instead of betting on one exact spelling.
  if (code.includes('INTEGRITY') || code.includes('CONSTRAINT')) {
    return SYSTEM_FAULT
  }

  // A Hebrew message is written for this user; an English one is written for us
  // and belongs in the log, not on the screen.
  const message = readMessage(body)
  if (HEBREW.test(message)) return message

  const status = response.status ?? body.statusCode ?? 0
  if (status === 401) return SESSION_ENDED
  if (status >= 500) return SERVER_FAULT

  return GENERIC
}

const LOGIN_EXPIRED = 'פג תוקף ההתחברות. נא להתחבר מחדש'

// `POST /api/auth/verify-otp` and `POST /api/auth/resend-otp`: the Gateway names each refusal with a code.
const OTP_MESSAGE_BY_CODE: Record<string, string> = {
  OTP_INVALID: 'הקוד שגוי או שפג תוקפו',
  OTP_NOT_PENDING: 'אין כניסה שממתינה לקוד. נא להתחבר מחדש',
  OTP_COOLDOWN: 'ביקשתם קוד לפני רגע. נא להמתין דקה ולנסות שוב',
  OTP_SEND_LIMIT: 'נשלחו יותר מדי קודים. נא לנסות שוב מאוחר יותר',
  OTP_VERIFY_LIMIT: 'חרגתם ממספר הניסיונות לקוד הזה. נא לבקש קוד חדש',
  UNAUTHORIZED: LOGIN_EXPIRED,
  USER_NOT_FOUND: UNKNOWN_USER,
  INTERNAL_ERROR: SERVER_FAULT,
}

// Pass null when the page has no login challenge, so there is nothing to send.
export const otpErrorMessage = (err: unknown): string => {
  if (err === null) return LOGIN_EXPIRED
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response
  if (!response) return NO_CONNECTION

  const body = readBody(response.data)
  const mapped = OTP_MESSAGE_BY_CODE[readCode(body).toUpperCase()]
  if (mapped) return mapped

  const message = readMessage(body)
  if (HEBREW.test(message)) return message

  const status = response.status ?? body.statusCode ?? 0
  if (status === 401) return LOGIN_EXPIRED
  if (status >= 500) return SERVER_FAULT
  return 'שגיאה באימות הקוד. נא לנסות שוב'
}
